// Phone number helpers. Browser-safe, no dependencies.

/** NANP rule: area code (NXX) and exchange (NXX) both start with 2-9. */
const NANP_TEN = /^[2-9]\d{2}[2-9]\d{6}$/;

/**
 * Normalise a user-typed phone number to E.164.
 * - keeps digits and a leading `+` only
 * - 10 digits -> `+1` + digits (US/Canada), NANP rule enforced
 * - 11 digits starting with 1 -> `+` + digits, NANP rule enforced on the last 10
 * - leading `+` with 8-15 digits -> as-is
 * - `00` international prefix -> `+`
 * - anything else -> null
 */
export function normalizeE164(input: string, defaultCountry: 'US' = 'US'): string | null {
  if (typeof input !== 'string') return null;
  void defaultCountry; // only 'US' today; the parameter fixes the signature for later countries
  const trimmed = input.trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D+/g, '');

  if (hasPlus) {
    // WHY (review round 2): "+239 555 9405" used to be kept as +2395559405 — a SECOND identity for
    // the same customer that no opt-out covers, and a real dial to country code 239 (Sao Tome) on a
    // consumer phone plan. A '+' in front of ten NANP digits is a typing habit, not a country code.
    if (digits.length === 10 && NANP_TEN.test(digits)) return '+1' + digits;
    if (digits.length === 11 && digits.startsWith('1') && NANP_TEN.test(digits.slice(1))) return '+' + digits;
    // Same rule as the database's e164 domain: no country code starts with 0.
    return digits.length >= 8 && digits.length <= 15 && digits[0] !== '0' ? '+' + digits : null;
  }
  if (digits.startsWith('00')) {
    const rest = digits.slice(2);
    return rest.length >= 8 && rest.length <= 15 && rest[0] !== '0' ? '+' + rest : null;
  }
  if (digits.length === 10) {
    return NANP_TEN.test(digits) ? '+1' + digits : null;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return NANP_TEN.test(digits.slice(1)) ? '+' + digits : null;
  }
  return null;
}

/** `+12395550100` -> `(239) 555-0100`; anything that is not a +1 number is returned unchanged. */
export function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (!m) return e164;
  return `(${m[1]}) ${m[2]}-${m[3]}`;
}

/** The three digits after `+1`, or null for anything that is not a NANP number. */
export function areaCodeOf(e164: string): string | null {
  const m = /^\+1(\d{3})\d{7}$/.exec(e164);
  return m ? m[1] : null;
}
